import { Component } from 'react';

import { lazyInject } from '../../../context/module';
import { container } from '../../../context/module';
import { FileUpload } from '../../../models/file-upload';
import { Logger } from '../../../models/logger';
import { FileManagerService } from '../../../services/file-manager.service';
import { MetricsService } from '../../../services/metrics.service';
import { ScriptLoadService } from '../../../services/script-load.service';
import { SpinnerService } from '../../../services/spinner.service';
import { MetricEventName } from '../../../types/metric-events';
import { PageIdentity, recentOnThisPage } from '../../../utils/recent-on-page';
import { untilDestroyed } from '../../../utils/until-destroyed';
import { openStudioReedit } from '../../studio/export/reedit';
import Modal from '../../ui/modal/modal.component';
import { FileUploadReEditModalComponent } from '../../ui/file-upload-reedit/file-upload-reedit-modal.component';
import styles from './recent-on-page.component.module.scss';

interface RecentOnPageProps extends PageIdentity {
    /** Shown in the heading, e.g. "Compress GIF". */
    toolName?: string;
    pageSlug?: string;
    /** Tells the parent how many rows this will render, so one shared trigger can count them. */
    onCount?: (n: number) => void;
}

interface RecentOnPageState {
    files: FileUpload[];
}

/**
 * "You converted these here — change a setting and run it again."
 *
 * Re-edit re-stages an already-uploaded file with different options and re-runs it. It is the
 * single most common thing a user wants next — 96.5% of continuations are same-tool re-runs,
 * not switches to another tool (docs/research/next-tool-transitions.md) — and it existed only
 * on `/downloads`, mixed in with every other file in the session. Someone who wants a slightly
 * smaller GIF had to leave the page whose settings they were just using, find their file among
 * the rest, and edit it there.
 *
 * Rendered as a section of the recent-files panel rather than a block of its own. As a separate
 * block it sat below the tool with its own heading and border, so a returning visitor met two
 * near-identical panels -- this one and "Files you used recently" -- doing different things under
 * labels that did not distinguish them. One trigger, two labelled sections.
 * See docs/proposed/RETENTION_FIXES.md T3.3.
 */
export class RecentOnPageComponent extends Component<RecentOnPageProps, RecentOnPageState> {
    private readonly logger = Logger.getLogger('RecentOnPageComponent');

    @lazyInject(FileManagerService) private fileManagerService: FileManagerService;
    @lazyInject(ScriptLoadService) private scriptLoadService: ScriptLoadService;
    @lazyInject(SpinnerService) private spinner: SpinnerService;

    readonly state: Readonly<RecentOnPageState> = { files: [] };

    private get identity(): PageIdentity {
        return {
            processType: this.props.processType,
            toType: this.props.toType,
            uniqueProcessType: this.props.uniqueProcessType,
        };
    }

    componentDidMount() {
        this.refresh(this.fileManagerService.getFileUploads());
        this.fileManagerService.fileUploadsListChanged
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (fileUploads) => this.refresh(fileUploads),
                error: (e) => this.logger.error({ message: 'Recent-on-page list failed', error: e }),
            });
    }

    componentWillUnmount() {
        // for untilDestroyed
    }

    private refresh(fileUploads: FileUpload[]) {
        const files = recentOnThisPage(fileUploads, this.identity);
        // Only re-render when the set actually changes: fileUploadsListChanged fires on every
        // progress tick during an upload, and this block sits under a live tool.
        const same = files.length === this.state.files.length
            && files.every((f, i) => f.id === this.state.files[i]?.id);
        if (!same) {
            this.setState({ files });
            this.props.onCount?.(files.length);
        }
    }

    private async reedit(fileUpload: FileUpload) {
        try {
            container.get(MetricsService).emit(MetricEventName.RECENT_REEDIT_CLICKED, {
                pageSlug: this.props.pageSlug || '',
                toType: `${fileUpload.toType || ''}`,
            });
        } catch { /* telemetry is never load-bearing */ }

        // §11.0 R11 — same branch as the /downloads card, same helper, same answer: a Studio
        // workflow re-opens in the Studio, not in the merge modal.
        if (openStudioReedit(fileUpload)) return;

        // Re-edit re-runs a conversion, which needs the upload/websocket stack the tool page
        // only loads lazily. Without this the modal opens onto a dead Apply button.
        await this.spinner.runWithLoader(async () => {
            try {
                await this.scriptLoadService.loadAllScriptsIfNotLoaded();
            } catch (e) {
                this.logger.error({ message: 'Something went wrong while loading scripts.', error: e });
            }
        }, 'Please wait while we initialize file upload service...');

        Modal.createModal(FileUploadReEditModalComponent, { fileUpload });
    }

    render() {
        const { files } = this.state;
        if (files.length === 0) return null;

        // A section of the recent-files panel, not a panel of its own. It used to render its own
        // heading and border further down the page, so a returning visitor met two near-identical
        // blocks doing different things under labels that did not distinguish them.
        return (
            <div className={styles.recent}>
                <h3 className={styles.heading}>Re-run a conversion from this page</h3>
                <p className={styles.sub}>
                    Change a setting and run it again — no need to upload anything.
                </p>
                <ul className={styles.list}>
                    {files.map(f => (
                        <li key={f.id} className={styles.row}>
                            <span className={styles.name}>{f.name}</span>
                            <button type="button" className={styles.action} onClick={() => this.reedit(f)}>
                                Re-edit
                            </button>
                        </li>
                    ))}
                </ul>
            </div>
        );
    }
}
